import { Box, Typography, useColorScheme } from "@mui/joy";

import type { Step } from "../../HowItWorks";
import { FadeIn } from "~/common/components";

interface StepCardProps {
  step: Step;
  index: number;
}

export const StepCard = ({ step, index }: StepCardProps) => {
  const { colorScheme } = useColorScheme();
  const isReversed = index % 2 === 1;
  const image = colorScheme === "dark" ? step.imageDark : step.imageLight;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: { xs: 4, md: 6 },
        alignItems: "center",
        py: { xs: 4, md: 4 },
      }}
    >
      {/* Text Content */}
      <FadeIn
        offsetY={30}
        duration={400}
        delay={300}
        sx={{
          order: { xs: 1, md: isReversed ? 2 : 1 },
        }}
      >
        <Box sx={{ marginX: "auto", maxWidth: "48ch" }}>
          <Typography
            sx={{
              fontSize: { xs: "3rem", md: "4rem", lg: "5rem" },
              fontWeight: 700,
              lineHeight: 1,
              color: "primary.300",
              opacity: 0.4,
              mb: 2,
              "[data-joy-color-scheme='dark'] &": {
                color: "primary.400",
                opacity: 0.3,
              },
            }}
          >
            {step.number}
          </Typography>
          <Typography
            level="h3"
            sx={{
              fontSize: { xs: "1.25rem", md: "1.5rem", lg: "1.75rem" },
              fontWeight: 700,
              mb: 2,
              lineHeight: 1.3,
            }}
          >
            {step.title}
          </Typography>
          <Typography
            textColor="text.secondary"
            sx={{
              fontSize: { xs: "0.875rem", md: "1rem" },
              lineHeight: 1.75,
            }}
          >
            {step.description}
          </Typography>
        </Box>
      </FadeIn>

      {/* Image with geometric background */}
      <FadeIn
        offsetY={30}
        duration={400}
        delay={300}
        sx={{
          order: { xs: 2, md: isReversed ? 1 : 2 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: { xs: 280, md: 360, lg: 420 },
          }}
        >
          {/* Geometric background shape */}
          <Box
            sx={{
              position: "absolute",
              width: { xs: "85%", md: "80%" },
              height: { xs: "85%", md: "80%" },
              borderRadius: "50%",
              background:
                colorScheme === "dark"
                  ? "radial-gradient(circle, rgba(var(--joy-palette-primary-mainChannel) / 0.15) 0%, transparent 70%)"
                  : "radial-gradient(circle, rgba(var(--joy-palette-primary-mainChannel) / 0.12) 0%, transparent 70%)",
              zIndex: 0,
            }}
          />

          {/* Decorative ring */}
          <Box
            sx={{
              position: "absolute",
              width: { xs: "70%", md: "65%" },
              height: { xs: "70%", md: "65%" },
              borderRadius: "50%",
              border: "2px solid",
              borderColor:
                colorScheme === "dark"
                  ? "rgba(var(--joy-palette-primary-mainChannel) / 0.2)"
                  : "rgba(var(--joy-palette-primary-mainChannel) / 0.15)",
              zIndex: 0,
            }}
          />

          {/* Image with perspective */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width:
                index === 0
                  ? { xs: "95%", sm: "90%", md: "100%" }
                  : { xs: "90%", sm: "80%", md: "85%" },
              maxWidth: index === 0 ? 520 : 400,
              transform: isReversed
                ? "perspective(800px) rotateY(10deg) rotateX(-4deg)"
                : "perspective(800px) rotateY(-10deg) rotateX(4deg)",
              transition: "transform 0.3s ease-out",
              "&:hover": {
                transform: isReversed
                  ? "perspective(800px) rotateY(5deg) rotateX(-2deg) scale(1.02)"
                  : "perspective(800px) rotateY(-5deg) rotateX(2deg) scale(1.02)",
              },
            }}
          >
            {/* Liquid glass wrapper */}
            <Box
              sx={{
                p: "15px",
                borderRadius: "xl",
                background:
                  colorScheme === "dark"
                    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow:
                  colorScheme === "dark"
                    ? "0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.1)"
                    : "0 25px 50px -12px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.5)",
              }}
            >
              <Box
                sx={{
                  borderRadius: "lg",
                  overflow: "hidden",
                }}
              >
                <img
                  src={image}
                  alt={step.title}
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </FadeIn>
    </Box>
  );
};
